import React, { useState, useEffect, useRef } from 'react';
import html2canvas from 'html2canvas';
import { Loader, X, AlertTriangle, CheckCircle, Share2 } from 'lucide-react';
import OzmapDisplay from '../common/OzmapDisplay';
import { useOzmapData } from '../../hooks/useOzmapData';

const TradeShareModal = ({ isOpen, onClose, trade }) => {
    const [status, setStatus] = useState('loading'); // loading, waiting_tiles, capturing, ready_to_share, shared, error
    const [message, setMessage] = useState('Carregando dados do mapa...');
    const [canClose, setCanClose] = useState(false);
    const [capturedFile, setCapturedFile] = useState(null);
    const containerRef = useRef(null);

    // Fetch data specifically for this modal (300m radius as requested)
    const { ozmapData, isLoading: isOzmapLoading, error: ozmapError } = useOzmapData({
        projectName: trade?.city ? trade.city.toUpperCase() : '',
        centerCoordinates: trade?.coordinates,
        radius: 300
    });

    // Timeout de segurança (30s)
    useEffect(() => {
        if (!isOpen) return;

        const timeoutId = setTimeout(() => {
            if (status !== 'shared' && status !== 'ready_to_share' && status !== 'error') {
                setStatus('error');
                setMessage('Tempo limite excedido. O carregamento demorou muito.');
                setCanClose(true);
            }
        }, 30000);

        return () => clearTimeout(timeoutId);
    }, [isOpen, status]);

    // Reset state on open
    useEffect(() => {
        if (isOpen) {
            setStatus('loading');
            setMessage('Carregando dados do mapa...');
            setCanClose(false);
            setCapturedFile(null);
        }
    }, [isOpen]);

    // Monitor loading state
    useEffect(() => {
        if (!isOpen) return;

        if (ozmapError) {
            setStatus('error');
            setMessage(`Erro ao carregar dados: ${ozmapError.message}`);
            setCanClose(true);
            return;
        }

        if (!isOzmapLoading && status === 'loading' && ozmapData) {
            // Data loaded, switch to waiting for tiles
            setStatus('waiting_tiles');
            setMessage('Renderizando mapa...');

            // Give map tiles some time to render completely
            setTimeout(() => {
                setStatus('capturing');
            }, 5000);
        }
    }, [isOpen, isOzmapLoading, ozmapError, ozmapData, status]);

    // Auto Capture (but wait for user to share)
    useEffect(() => {
        if (status === 'capturing' && containerRef.current) {
            const captureImage = async () => {
                try {
                    setMessage('Gerando imagem...');
                    const canvas = await html2canvas(containerRef.current, {
                        useCORS: true,
                        allowTaint: true,
                        scale: 2,
                        ignoreElements: (element) => element.classList.contains('no-capture')
                    });

                    const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
                    const file = new File([blob], 'mapa_troca.png', { type: 'image/png' });
                    setCapturedFile(file);

                    setStatus('ready_to_share');
                    setMessage('Pronto para compartilhar');
                    setCanClose(true);
                } catch (err) {
                    console.error('Erro ao gerar imagem:', err);
                    setStatus('error');
                    setMessage('Erro ao gerar a imagem do mapa.');
                    setCanClose(true);
                }
            };

            captureImage();
        }
    }, [status]);

    const handleShareClick = async () => {
        if (!capturedFile) return;

        const shareText = `Troca de poste(s) em andamento nessa região: ${trade.ftthSector || 'N/A'}. Enviado via Gestor de Projetos.`;

        try {
            if (navigator.share && navigator.canShare && navigator.canShare({ files: [capturedFile] })) {
                await navigator.share({
                    files: [capturedFile],
                    text: shareText
                });
                setMessage('Compartilhado com sucesso!');
                setStatus('shared');
            } else {
                // Fallback
                const url = URL.createObjectURL(capturedFile);
                const a = document.createElement('a');
                a.href = url;
                a.download = 'mapa_troca.png';
                a.click();
                URL.revokeObjectURL(url);

                await navigator.clipboard.writeText(shareText);
                setMessage('Imagem baixada e texto copiado!');
                setStatus('shared');
            }
        } catch (err) {
            console.error('Erro ao compartilhar:', err);
            // Don't change status to error here, just notify user
            setMessage('Erro ao abrir menu de compartilhamento.');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[1100] flex justify-center items-center p-4 overflow-y-auto">
            <div className="relative w-full max-w-4xl flex flex-col items-center my-auto min-h-0">

                {/* Close Button - Always visible on top right for safety */}
                {canClose && (
                    <button
                        onClick={onClose}
                        className="absolute -top-12 right-0 p-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors z-[1200]"
                        aria-label="Fechar"
                    >
                        <X size={28} />
                    </button>
                )}

                {/* Status Header */}
                <div className="mb-4 bg-white dark:bg-slate-800 rounded-full px-6 py-2 shadow-lg flex items-center gap-3 shrink-0">
                    {status === 'loading' || status === 'waiting_tiles' || status === 'capturing' ? (
                        <Loader className="animate-spin text-blue-500" />
                    ) : status === 'shared' ? (
                        <CheckCircle className="text-green-500" />
                    ) : status === 'ready_to_share' ? (
                        <Share2 className="text-blue-500" />
                    ) : (
                        <AlertTriangle className="text-red-500" />
                    )}
                    <span className="font-medium text-slate-700 dark:text-slate-200 text-sm sm:text-base whitespace-nowrap overflow-hidden text-ellipsis max-w-[200px] sm:max-w-none">
                        {message}
                    </span>
                </div>

                {/* Map Container - Aspect Ratio logic - Adjusted for mobile fitting */}
                <div
                    ref={containerRef}
                    className="w-full relative bg-white rounded-lg overflow-hidden shadow-2xl aspect-[9/16] md:aspect-[16/9] max-h-[55vh] shrink-0"
                >
                    <OzmapDisplay
                        trade={trade}
                        ozmapData={ozmapData}
                        darkMapEnabled={false} // Force Light Mode
                        isVisible={true}
                        isLoading={false} // Managed externally
                        error={null}
                        zoom={17} // Closer zoom
                        className="h-full"
                    />

                    {/* Overlay Info (captured in screenshot) */}
                    <div className="absolute bottom-4 left-4 right-4 bg-white/90 p-3 sm:p-4 rounded-lg shadow-lg border-l-4 border-orange-500 z-[1000]">
                        <h3 className="font-bold text-slate-800 text-base sm:text-lg">Troca de Poste em Andamento</h3>
                        <p className="text-slate-600 text-xs sm:text-base"><strong>Região:</strong> {trade.ftthSector || 'N/A'}</p>
                        <p className="text-slate-600 text-xs sm:text-base"><strong>Endereço:</strong> {trade.address || 'N/A'}</p>
                    </div>
                </div>

                {/* Footer Controls */}
                <div className="mt-4 sm:mt-6 flex flex-wrap justify-center gap-4 shrink-0 pb-4">
                    {status === 'ready_to_share' && (
                        <button
                            onClick={handleShareClick}
                            className="btn-primary flex items-center bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-full shadow-lg transform hover:scale-105 transition-all"
                        >
                            <Share2 className="mr-2" size={24}/> Compartilhar via WhatsApp
                        </button>
                    )}

                    {canClose && (
                        <button
                            onClick={onClose}
                            className="btn-secondary flex items-center bg-white hover:bg-slate-100 text-slate-800 px-6 py-3 rounded-full shadow-lg"
                        >
                            <X className="mr-2" size={20}/> Fechar
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TradeShareModal;
